import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Selectors
import { getOneOnOneWorkspaces } from 'Shared/selectors/workspace';

// Components
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpModal, WpModalBody, WpModalFooter, WpModalHeader } from 'Wpds/components/modal';
import WpUserInput, { WidthType } from 'Wpds/components/user-input/WpUserInput';
import WpIcon from 'Wpds/components/icon/WpIcon';
import AddTeammateByEmailModal from 'Shared/components/modals/AddTeammateByEmailModal/AddTeammateByEmailModal';

// Styles
import './SelectUserModal.scss';

export type FlowType = 'kudos' | 'feedback';
const DEFAULT_FLOW_TYPE = 'feedback';

type Props = {
  isOpen: boolean;
  toggleModal: () => void;
  handleContinue: Function;
  type?: FlowType;
};

const SelectUserModal = ({ isOpen, toggleModal, handleContinue, type = DEFAULT_FLOW_TYPE }: Props) => {
  const { id: currentUserId, isThirdPartyUser } = useCurrentUser();
  const { tr } = useTranslation();
  const [selectedUser, setSelectedUser] = useState(null);
  const oneOnOneWorkspacesOnly = type === 'feedback';
  const oneOnOneWorkspaces = useSelector(state => getOneOnOneWorkspaces(state));
  const oneOnOneAndGroupWorkspaces = useSelector(state => state.workspaces.filter(w => !w.isPersonal));
  const workspaces = oneOnOneWorkspacesOnly ? oneOnOneWorkspaces : oneOnOneAndGroupWorkspaces;

  const [isShowingInviteModal, toggleIsShowingInviteModal] = useToggle(false);

  const oneOnOneWithoutOtherUser = oneOnOneWorkspaces
    .filter(ws => ws.users.length === 1)
    .map(ws => {
      return { displayName: ws.name, id: ws.id, workspaceId: ws.id, isWorkspace: true };
    });

  const users = uniqBy(
    flatten(
      workspaces.map(({ users, id }) =>
        users.map(user => {
          return { ...user, workspaceId: id };
        })
      )
    ).filter(({ id }) => id !== currentUserId),
    'id'
  );

  const handleSelectUser = selectedUser => {
    setSelectedUser(selectedUser);
  };

  const handleContinueClick = () => {
    handleContinue(selectedUser);
  };

  const combinedUsers = [...users, ...(type === 'kudos' ? oneOnOneWithoutOtherUser : [])];

  const showSearch = combinedUsers.length > 8;

  const baseClassName = 'SelectUserModal';
  const customDropdownFooter = () => (
    <div className={`${baseClassName}__invite-dropdown-action`} onClick={toggleIsShowingInviteModal}>
      <div className={`${baseClassName}__invite-dropdown-action__icon`}>
        <WpIcon name="plus-circle" />
      </div>
      <span>{tr('perform.give_feedback.select_user_modal.invite_teammate')}</span>
    </div>
  );

  return (
    <>
      <WpModal size="sm" isOpen={isOpen} onToggle={toggleModal}>
        <WpModalHeader>{tr('perform.give_feedback.select_user_modal.header')}</WpModalHeader>
        <WpModalBody>
          {tr('perform.give_feedback.select_user_modal.body', type)}
          <br />
          <br />
          <WpUserInput
            id="select-user-for-feedback"
            label={tr('perform.give_feedback.select_user_modal.select_user_label')}
            users={combinedUsers}
            value={selectedUser?.id}
            onChange={handleSelectUser}
            showSearch={showSearch}
            labelHidden
            placeholder={tr('perform.give_feedback.select_user_modal.select_teammate_placeholder')}
            optionsWidth={WidthType.target}
            components={{
              dropdownFooter: !isThirdPartyUser && customDropdownFooter,
            }}
          />
        </WpModalBody>
        <WpModalFooter>
          <WpButtonGroup>
            <WpButton variant="tertiary" onClick={toggleModal}>
              {tr('perform.common.cancel')}
            </WpButton>
            <WpButton disabled={!selectedUser} onClick={handleContinueClick}>
              {tr('perform.common.continue')}
            </WpButton>
          </WpButtonGroup>
        </WpModalFooter>
      </WpModal>
      {isShowingInviteModal && (
        <AddTeammateByEmailModal
          isOpen={isShowingInviteModal}
          onClose={toggleIsShowingInviteModal}
          submitButtonText={tr('perform.give_feedback.select_user_modal.invite_teammates_button')}
          redirectOnSuccess={false}
        />
      )}
    </>
  );
};

export default SelectUserModal;
